import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CrudList, CrudStyles } from '../shared/Crud';
import addressTemplateService from '../../services/addressTemplateService';
import { formatZipDisplay } from '../../utils/mapAddressFromApi';
import { addressPlaceLabel } from '../../utils/addressName';
import { AddressPlaceColumnHeader } from '../shared/AddressNameField';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { SELECTOR_MODAL_HINT, SelectorModalHint } from './SelectorModalHint';

const { FilterGroup } = CrudStyles;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${({ $zIndex }) => $zIndex ?? 9999};
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: #fff;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    max-width: min(900px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    overflow: hidden;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-shrink: 0;

    h2 {
        margin: 0;
        font-size: 18px;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
    }
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    font-size: 24px;
    line-height: 1;
    &:hover {
        color: #222;
    }
`;

const TEMPLATE_COLUMNS = [
    {
        header: <AddressPlaceColumnHeader />,
        accessor: 'place',
        render: (row) => addressPlaceLabel(row),
    },
    { header: 'Número', accessor: 'number' },
    { header: 'Bairro', accessor: 'district' },
    { header: 'Cidade', accessor: 'city' },
    {
        header: 'UF',
        accessor: 'state',
        render: (row) => row?.state?.abbreviation ?? '—',
    },
    {
        header: 'CEP',
        accessor: 'zipCode',
        render: (row) => formatZipDisplay(row?.zipCode),
    },
];

export default function AddressTemplateSelectorModal({
    open,
    customerId,
    /** Transportadora envia customerId na query; cliente logado omite. */
    includeCustomerId = false,
    onClose,
    onSelect,
    zIndex = 9999,
}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });
    const [nameInput, setNameInput] = useState('');
    const [appliedName, setAppliedName] = useState('');
    const appliedNameRef = useRef('');
    const debouncedName = useDebouncedValue(open ? nameInput : '', 400);

    useEffect(() => {
        if (!open) {
            appliedNameRef.current = '';
            setNameInput('');
            setAppliedName('');
            setData([]);
            setPagination({ page: 0, totalPages: 0, first: true, last: true });
            setLoadError(null);
        }
    }, [open]);

    useEffect(() => {
        const next = debouncedName.trim();
        if (appliedNameRef.current === next) return;
        appliedNameRef.current = next;
        setAppliedName(next);
        setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    }, [debouncedName]);

    useEffect(() => {
        if (!open) return;
        if (includeCustomerId && (customerId == null || String(customerId).trim() === '')) {
            setLoadError('Selecione o cliente da entrega para listar endereços recorrentes.');
            setData([]);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const result = await addressTemplateService.list({
                    customerId: includeCustomerId ? customerId : undefined,
                    page: pagination.page,
                    size: 10,
                    name: appliedName,
                });
                if (cancelled) return;
                const content = result.content ?? [];
                const pageMeta = result.page ?? {};
                const totalPages = pageMeta.totalPages ?? (content.length === 0 ? 0 : 1);
                const pageNumber = pageMeta.number ?? pagination.page;
                setData(content);
                setPagination({
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                });
            } catch (e) {
                if (!cancelled) {
                    setLoadError(e?.message || 'Não foi possível carregar os endereços recorrentes.');
                    setData([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, includeCustomerId, customerId, pagination.page, appliedName]);

    const handlePageChange = (newPage) => {
        setPagination((p) => ({ ...p, page: newPage }));
    };

    if (!open) return null;

    return (
        <Overlay
            $zIndex={zIndex}
            role="dialog"
            aria-modal="true"
            aria-labelledby="address-template-modal-title"
            onClick={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <h2 id="address-template-modal-title">Selecionar endereço recorrente</h2>
                    <CloseBtn type="button" onClick={() => onClose?.()} title="Fechar">
                        ×
                    </CloseBtn>
                </ModalHeader>
                <SelectorModalHint>{SELECTOR_MODAL_HINT}</SelectorModalHint>

                {loadError && (
                    <div style={{ marginBottom: 12 }} role="alert">
                        <p style={{ margin: 0, color: '#e74c3c', fontSize: 14 }}>{loadError}</p>
                    </div>
                )}

                <CrudList
                    hideHeader
                    title="Endereços recorrentes"
                    tableMinWidth="640px"
                    filters={(
                        <FilterGroup>
                            <label htmlFor="address-template-modal-name">Nome</label>
                            <input
                                id="address-template-modal-name"
                                name="name"
                                value={nameInput}
                                onChange={(event) => setNameInput(event.target.value)}
                                placeholder="Buscar por nome..."
                                maxLength={100}
                                autoComplete="off"
                            />
                        </FilterGroup>
                    )}
                    columns={TEMPLATE_COLUMNS}
                    data={data}
                    loading={loading}
                    onRowClick={(item) => onSelect?.(item)}
                    pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                    }}
                />
            </ModalBox>
        </Overlay>
    );
}
